// Audio calls (FigJam's "audio"): a peer-to-peer WebRTC mesh among the people in a file. The
// multiplayer socket relays signaling; media flows directly between browsers. Without
// server-configured STUN/TURN servers, calls connect on the local network only.
import type { Presence } from '@vellum/core';

interface CallHost {
  me(): string | null;
  people(): Map<string, Presence>;
  presence(p: Partial<Presence>): void;
  signal(to: string, data: unknown): void;
  changed(): void;
  toast(message: string): void;
}

type Signal = { sdp?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit };

export class AudioCall {
  local: MediaStream | null = null;
  muted = false;
  ice: RTCIceServer[] = [];
  private pcs = new Map<string, RTCPeerConnection>();
  private players = new Map<string, HTMLAudioElement>();
  /** who we hear right now (connection state per peer) */
  readonly state = new Map<string, RTCPeerConnectionState>();
  constructor(private host: CallHost) {}

  get joined() {
    return !!this.local;
  }

  async join() {
    if (this.local) return;
    try {
      this.local = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch {
      this.host.toast('Microphone access is needed to join the call');
      return;
    }
    this.muted = false;
    this.host.presence({ audio: { muted: false } });
    for (const p of this.host.people().values()) if (p.audio && p.kind === 'human') this.meet(p.clientId);
    this.host.changed();
  }

  leave() {
    for (const t of this.local?.getTracks() ?? []) t.stop();
    this.local = null;
    for (const id of [...this.pcs.keys()]) this.drop(id);
    this.host.presence({ audio: null });
    this.host.changed();
  }

  toggleMute() {
    if (!this.local) return;
    this.muted = !this.muted;
    for (const t of this.local.getAudioTracks()) t.enabled = !this.muted;
    this.host.presence({ audio: { muted: this.muted } });
    this.host.changed();
  }

  /** Someone's presence changed: connect when they join the call, hang up when they leave. */
  onPresence(p: Presence, prev?: Presence) {
    if (!this.local) return;
    if (p.audio && !prev?.audio) this.meet(p.clientId);
    if (!p.audio && prev?.audio) this.drop(p.clientId);
  }

  onLeave(id: string) {
    this.drop(id);
  }

  async onSignal(from: string, data: Signal) {
    if (!this.local) return;
    const pc = this.pcs.get(from) ?? this.connection(from);
    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        if (data.sdp.type === 'offer') {
          await pc.setLocalDescription(await pc.createAnswer());
          this.host.signal(from, { sdp: pc.localDescription!.toJSON() });
        }
      } else if (data.candidate) await pc.addIceCandidate(data.candidate);
    } catch (e) {
      console.warn('[vellum] audio signaling failed', e);
    }
  }

  /** The peer with the lower id makes the offer, so two people never offer to each other at once. */
  private meet(id: string) {
    const me = this.host.me();
    if (this.pcs.has(id) || !me) return;
    const pc = this.connection(id);
    if (me < id)
      void (async () => {
        await pc.setLocalDescription(await pc.createOffer());
        this.host.signal(id, { sdp: pc.localDescription!.toJSON() });
      })();
  }

  private connection(id: string): RTCPeerConnection {
    const pc = new RTCPeerConnection({ iceServers: this.ice });
    for (const t of this.local!.getTracks()) pc.addTrack(t, this.local!);
    pc.onicecandidate = (e) => e.candidate && this.host.signal(id, { candidate: e.candidate.toJSON() });
    pc.ontrack = (e) => {
      let el = this.players.get(id);
      if (!el) {
        el = new Audio();
        el.autoplay = true;
        this.players.set(id, el);
      }
      el.srcObject = e.streams[0] ?? new MediaStream([e.track]);
      void el.play().catch(() => {});
    };
    pc.onconnectionstatechange = () => {
      this.state.set(id, pc.connectionState);
      this.host.changed();
    };
    this.pcs.set(id, pc);
    return pc;
  }

  private drop(id: string) {
    this.pcs.get(id)?.close();
    this.pcs.delete(id);
    this.state.delete(id);
    const el = this.players.get(id);
    if (el) {
      el.srcObject = null;
      this.players.delete(id);
    }
    this.host.changed();
  }
}
